import {
  DEFAULT_LIBRARY_URL,
  ENCODED_HASH,
} from '../../../../../constants/library';
import {
  LoggedOutFullLibraryData,
  LoggedOutProductLibraryItem,
  SelectedLibraryItem,
} from '../../../../../types/library';

/**
 * Get the initial L2 item to render.
 */
export const getInitialItem = (fullLibrary?: LoggedOutFullLibraryData) => {
  let item: SelectedLibraryItem | undefined;
  fullLibrary?.productLibrary.some((lib) => {
    if ('navs' in lib) {
      return lib.navs?.some((l1) => {
        return l1.contains?.some((l2Item) => {
          item = {
            l1,
            l2: l2Item,
          };
          return true;
        });
      });
    } else if ('contains' in lib) {
      return lib.contains?.some((l2Item) => {
        item = {
          l1: lib,
          l2: l2Item,
        };
        return true;
      });
    }

    return false;
  });

  return item;
};

/**
 * Get the l1 and l2 items that match the given l1Name and l2Name.
 */
export const getMatchingItems = (
  fullLibrary: LoggedOutFullLibraryData,
  l1Name: string,
  l2Name: string,
): SelectedLibraryItem | undefined => {
  let item: SelectedLibraryItem | undefined;
  fullLibrary?.productLibrary.some((lib) => {
    if ('navs' in lib) {
      return lib.navs?.some((l1) => {
        if (l1.name === l1Name) {
          return l1.contains?.some((l2Item) => {
            if (l2Item.id === l2Name) {
              item = {
                l1,
                l2: l2Item,
              };
              return true;
            }
            return false;
          });
        }
        return false;
      });
    } else if ('contains' in lib) {
      if (lib.path === l1Name) {
        return lib.contains?.some((l2Item) => {
          if (l2Item.id === l2Name) {
            item = {
              l1: lib,
              l2: l2Item,
            };
            return true;
          }
          return false;
        });
      }
    }
    return false;
  });

  return item;
};

/**
 * Get the subset of the library to render.
 */
export const getLibrarySubset = (
  fullLibrary: LoggedOutFullLibraryData,
  selectedItem?: SelectedLibraryItem,
) => {
  const selectedLibs: LoggedOutProductLibraryItem[] = [];
  fullLibrary?.productLibrary.forEach((lib) => {
    // Handle grouped item
    if ('navs' in lib) {
      lib.navs?.forEach((l1) => {
        l1.contains?.forEach((l2) => {
          if (
            l2.id === selectedItem?.l2?.id &&
            l1.id === selectedItem?.l1?.id
          ) {
            selectedLibs.push({
              ...lib,
              navs: [
                {
                  ...l1,
                  contains: [l2],
                },
              ],
            });
          }
        });
      });
      // Handle contains item
    } else if ('contains' in lib) {
      lib.contains?.forEach((l2) => {
        if (l2.id === selectedItem?.l2?.id) {
          selectedLibs.push({
            ...lib,
            contains: [l2],
          });
        }
      });
    }
  });

  // Only render subset of the library for performance reasons
  const library = {
    ...fullLibrary,
    productLibrary: selectedLibs,
  };

  return library;
};

/**
 * Get the library link from the breadcrumb link.
 */
export const getLibraryLinkWithHash = (link = '') => {
  const clearLink = link.replace(DEFAULT_LIBRARY_URL, '');

  if (!clearLink) {
    return DEFAULT_LIBRARY_URL;
  }
  const [, productPath, ...restBreadcrumbs] = clearLink.split('/');

  if (!productPath) {
    return DEFAULT_LIBRARY_URL;
  }

  if (!restBreadcrumbs.length) {
    return `${DEFAULT_LIBRARY_URL}/${productPath}`;
  }

  const libraryLink = `${DEFAULT_LIBRARY_URL}/${productPath}/${ENCODED_HASH}${restBreadcrumbs.join(
    '/',
  )}`;
  return libraryLink;
};

/**
 * Remove hash from the string.
 */
export const removeHash = (str = '') => {
  return str.replace(/#|%23/g, '');
};

/**
 * Remove hash from the array of strings.
 */
export const removeHashArray = (params: string[] | string = []) => {
  if (typeof params === 'string') {
    return [removeHash(params)];
  }
  return params.map((param) => removeHash(param));
};
